import React, { useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';
import './AdminPanel.css';

const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
const API_BASE_URL = isProduction
  ? `${window.location.protocol}//${window.location.host}`
  : 'http://localhost:5000';

const socket = io(API_BASE_URL, {
  transports: ['websocket', 'polling'],
  reconnection: true
});

const ADMIN_PASSWORD = 'HaiMentor123#$';
const DAY_NAMES = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'];

// SVG Icons
const Icons = {
  overview: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/></svg>,
  mentors: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg>,
  messages: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/></svg>,
  reports: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/></svg>,
  download: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/></svg>,
  logout: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/></svg>
};

function AdminPanel() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [heatmapType, setHeatmapType] = useState('both');
  const [selectedMentorFilter, setSelectedMentorFilter] = useState('all');
  const [mentorsAdmin, setMentorsAdmin] = useState([]);
  const [selectedMentorModal, setSelectedMentorModal] = useState(null);
  const [deletingMentor, setDeletingMentor] = useState(null);
  const [reconnectingMentor, setReconnectingMentor] = useState(null);
  const [mentors, setMentors] = useState([]);
  const [selectedMentor, setSelectedMentor] = useState(null);
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingChats, setLoadingChats] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [reportType, setReportType] = useState('department');
  const [reportMentor, setReportMentor] = useState('all');
  const [reportDateRange, setReportDateRange] = useState('week');
  const [generatingReport, setGeneratingReport] = useState(false);
  const messagesEndRef = useRef(null);
  const reportRef = useRef(null);

  useEffect(() => {
    if (localStorage.getItem('adminAuth') === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    const loadData = () => {
      setLoading(true);
      socket.emit('admin_get_stats', { mentorId: selectedMentorFilter, dateRange: 'week' });
      socket.emit('get_mentors');
      socket.emit('get_mentors_admin');
    };

    loadData();
    const interval = setInterval(loadData, 30000);

    socket.on('admin_stats', (data) => { setStats(data); setLoading(false); });
    socket.on('mentors_list', (data) => setMentors(data || []));
    socket.on('mentors_admin_list', (data) => setMentorsAdmin(data || []));
    socket.on('admin_mentor_chats', (data) => { setChats(data.chats || []); setLoadingChats(false); });
    socket.on('admin_chat_messages', (data) => {
      setMessages(data.messages || []);
      setLoadingMessages(false);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    });
    socket.on('mentor_deleted', ({ success, mentorId }) => {
      if (success) {
        setMentorsAdmin(prev => prev.filter(m => m.id !== mentorId));
        setDeletingMentor(null);
        setSelectedMentorModal(null);
      }
    });
    socket.on('reconnect_result', ({ success }) => {
      setReconnectingMentor(null);
      if (success) socket.emit('get_mentors_admin');
    });
    socket.on('mentors_updated', () => {
      socket.emit('get_mentors_admin');
      socket.emit('get_mentors');
    });

    return () => {
      clearInterval(interval);
      socket.off('admin_stats');
      socket.off('mentors_list');
      socket.off('mentors_admin_list');
      socket.off('admin_mentor_chats');
      socket.off('admin_chat_messages');
      socket.off('mentor_deleted');
      socket.off('reconnect_result');
      socket.off('mentors_updated');
    };
  }, [isAuthenticated, selectedMentorFilter]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      localStorage.setItem('adminAuth', 'true');
    } else {
      setError('סיסמה שגויה');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('adminAuth');
  };

  const handleDeleteMentor = (mentorId) => {
    setDeletingMentor(mentorId);
    socket.emit('delete_mentor', { mentorId });
  };

  const handleReconnectMentor = (mentorId) => {
    setReconnectingMentor(mentorId);
    socket.emit('reconnect_mentor', { mentorId });
  };

  const handleSelectMentor = (mentor) => {
    setSelectedMentor(mentor);
    setSelectedChat(null);
    setMessages([]);
    setLoadingChats(true);
    socket.emit('admin_get_mentor_chats', { mentorId: mentor.id, dateFilter: 'all' });
  };

  const handleSelectChat = (chat) => {
    setSelectedChat(chat);
    setLoadingMessages(true);
    socket.emit('admin_get_chat_messages', { mentorId: selectedMentor.id, chatId: chat.id });
  };

  const getHeatmapData = (type = heatmapType, mentorStats = stats) => {
    if (!mentorStats) return null;
    if (type === 'sent') return mentorStats.sentActivity;
    if (type === 'received') return mentorStats.receivedActivity;
    return mentorStats.hourlyActivity;
  };

  const getHeatmapMax = (data) => {
    if (!data) return 1;
    let max = 0;
    data.forEach(day => day.forEach(val => { if (val > max) max = val; }));
    return max || 1;
  };

  const getHeatmapColor = (value, max) => {
    const intensity = value / max;
    if (intensity === 0) return 'rgba(0, 168, 132, 0.08)';
    if (intensity < 0.25) return 'rgba(0, 168, 132, 0.3)';
    if (intensity < 0.5) return 'rgba(0, 168, 132, 0.5)';
    if (intensity < 0.75) return 'rgba(0, 168, 132, 0.7)';
    return 'rgba(0, 168, 132, 1)';
  };

  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleString('he-IL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const generateReport = async () => {
    setGeneratingReport(true);

    // Wait for render
    await new Promise(resolve => setTimeout(resolve, 100));

    const printWindow = window.open('', '_blank');
    const mentorsToReport = reportType === 'mentor' && reportMentor !== 'all'
      ? activeMentors.filter(m => m.id === reportMentor)
      : activeMentors;

    let html = `
      <!DOCTYPE html>
      <html dir="rtl">
      <head>
        <meta charset="UTF-8">
        <title>דוח פעילות - ${new Date().toLocaleDateString('he-IL')}</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: Arial, sans-serif; padding: 20px; background: white; color: #333; }
          .report-header { text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #00a884; }
          .report-header h1 { color: #00a884; margin-bottom: 10px; }
          .report-header p { color: #666; }
          .mentor-section { page-break-inside: avoid; margin-bottom: 40px; padding: 20px; border: 1px solid #ddd; border-radius: 8px; }
          .mentor-name { font-size: 20px; font-weight: bold; color: #00a884; margin-bottom: 15px; padding-bottom: 10px; border-bottom: 1px solid #eee; }
          .heatmap-title { font-size: 14px; color: #666; margin: 15px 0 10px 0; }
          .heatmap-grid { display: grid; grid-template-columns: 30px repeat(24, 1fr); gap: 2px; margin-bottom: 20px; }
          .heatmap-corner { }
          .heatmap-hour { font-size: 8px; text-align: center; color: #999; }
          .heatmap-day { font-size: 10px; display: flex; align-items: center; justify-content: center; color: #666; }
          .heatmap-cell { aspect-ratio: 1; border-radius: 2px; display: flex; align-items: center; justify-content: center; font-size: 7px; color: white; min-height: 18px; }
          .stats-row { display: flex; gap: 20px; margin-bottom: 15px; }
          .stat-item { background: #f5f5f5; padding: 10px 15px; border-radius: 6px; }
          .stat-item strong { color: #00a884; font-size: 18px; }
          @media print {
            .mentor-section { break-inside: avoid; }
            body { padding: 10px; }
          }
        </style>
      </head>
      <body>
        <div class="report-header">
          <h1>דוח פעילות ${reportType === 'department' ? 'מחלקה' : 'מנטור'}</h1>
          <p>תאריך: ${new Date().toLocaleDateString('he-IL')} | תקופה: ${reportDateRange === 'week' ? 'שבוע אחרון' : reportDateRange === 'month' ? 'חודש אחרון' : 'היום'}</p>
        </div>
    `;

    mentorsToReport.forEach(mentor => {
      const sentData = stats?.sentActivity || Array(7).fill(Array(24).fill(0));
      const receivedData = stats?.receivedActivity || Array(7).fill(Array(24).fill(0));
      const sentMax = getHeatmapMax(sentData);
      const receivedMax = getHeatmapMax(receivedData);

      html += `
        <div class="mentor-section">
          <div class="mentor-name">${mentor.name}</div>
          <div class="stats-row">
            <div class="stat-item"><strong>${mentor.totalMessages || 0}</strong> הודעות סה"כ</div>
            <div class="stat-item"><strong>${mentor.totalChats || 0}</strong> צ'אטים</div>
            <div class="stat-item">${mentor.isConnected ? '🟢 מחובר' : '🔴 מנותק'}</div>
          </div>

          <div class="heatmap-title">הודעות שהתקבלו:</div>
          <div class="heatmap-grid">
            <div class="heatmap-corner"></div>
            ${[...Array(24)].map((_, i) => `<div class="heatmap-hour">${i}</div>`).join('')}
            ${receivedData.map((dayData, dayIndex) => `
              <div class="heatmap-day">${DAY_NAMES[dayIndex]}</div>
              ${dayData.map((value) => `
                <div class="heatmap-cell" style="background-color: ${getHeatmapColor(value, receivedMax)}">${value > 0 ? value : ''}</div>
              `).join('')}
            `).join('')}
          </div>

          <div class="heatmap-title">הודעות שנשלחו:</div>
          <div class="heatmap-grid">
            <div class="heatmap-corner"></div>
            ${[...Array(24)].map((_, i) => `<div class="heatmap-hour">${i}</div>`).join('')}
            ${sentData.map((dayData, dayIndex) => `
              <div class="heatmap-day">${DAY_NAMES[dayIndex]}</div>
              ${dayData.map((value) => `
                <div class="heatmap-cell" style="background-color: ${getHeatmapColor(value, sentMax)}">${value > 0 ? value : ''}</div>
              `).join('')}
            `).join('')}
          </div>
        </div>
      `;
    });

    html += `
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();

    setTimeout(() => {
      printWindow.print();
      setGeneratingReport(false);
    }, 500);
  };

  const activeMentors = mentorsAdmin.filter(m => m.is_active !== 0);
  const filteredChats = chats.filter(chat => chat.name?.toLowerCase().includes(searchQuery.toLowerCase()));
  const connectedCount = activeMentors.filter(m => m.isConnected).length;

  if (!isAuthenticated) {
    return (
      <div className="admin-login">
        <div className="login-card">
          <h1>Admin Panel</h1>
          <form onSubmit={handleLogin}>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="הזן סיסמה" autoFocus />
            {error && <p className="error">{error}</p>}
            <button type="submit">כניסה</button>
          </form>
        </div>
      </div>
    );
  }

  const renderHeatmap = (data, showNumbers = true) => {
    const max = getHeatmapMax(data);
    return (
      <div className="heatmap-grid">
        <div className="heatmap-corner"></div>
        {[...Array(24)].map((_, i) => <div key={i} className="heatmap-hour">{i}</div>)}
        {data?.map((dayData, dayIndex) => (
          <React.Fragment key={dayIndex}>
            <div className="heatmap-day">{DAY_NAMES[dayIndex]}</div>
            {dayData.map((value, hourIndex) => (
              <div key={hourIndex} className="heatmap-cell" style={{ backgroundColor: getHeatmapColor(value, max) }} title={`${value} הודעות`}>
                {showNumbers && value > 0 && <span>{value}</span>}
              </div>
            ))}
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <div className="admin-panel">
      {/* Desktop Sidebar */}
      <aside className="desktop-sidebar">
        <div className="sidebar-logo">Admin</div>
        <nav className="sidebar-nav">
          <button className={activeTab === 'overview' ? 'active' : ''} onClick={() => setActiveTab('overview')}>
            {Icons.overview}<span>סקירה</span>
          </button>
          <button className={activeTab === 'mentors' ? 'active' : ''} onClick={() => setActiveTab('mentors')}>
            {Icons.mentors}<span>מנטורים</span>
          </button>
          <button className={activeTab === 'messages' ? 'active' : ''} onClick={() => setActiveTab('messages')}>
            {Icons.messages}<span>הודעות</span>
          </button>
          <button className={activeTab === 'reports' ? 'active' : ''} onClick={() => setActiveTab('reports')}>
            {Icons.reports}<span>דוחות</span>
          </button>
        </nav>
        <button className="sidebar-logout" onClick={handleLogout}>
          {Icons.logout}<span>התנתק</span>
        </button>
      </aside>

      {/* Main Content */}
      <div className="admin-content">
        {/* Header */}
        <header className="admin-header">
          <h1>
            {activeTab === 'overview' && 'סקירה כללית'}
            {activeTab === 'mentors' && 'ניהול מנטורים'}
            {activeTab === 'messages' && 'הודעות'}
            {activeTab === 'reports' && 'דוחות'}
          </h1>
          <div className="header-controls">
            {activeTab === 'overview' && (
              <>
                <select value={selectedMentorFilter} onChange={(e) => setSelectedMentorFilter(e.target.value)} className="mentor-filter-select">
                  <option value="all">כל המנטורים</option>
                  {activeMentors.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
                <div className="heatmap-filters">
                  <button className={`filter-btn ${heatmapType === 'both' ? 'active' : ''}`} onClick={() => setHeatmapType('both')}>הכל</button>
                  <button className={`filter-btn ${heatmapType === 'sent' ? 'active' : ''}`} onClick={() => setHeatmapType('sent')}>נשלחו</button>
                  <button className={`filter-btn ${heatmapType === 'received' ? 'active' : ''}`} onClick={() => setHeatmapType('received')}>התקבלו</button>
                </div>
              </>
            )}
          </div>
        </header>

        {/* Main Content */}
        <main className="admin-main">
          {activeTab === 'overview' && (
            <div className="tab-content">
              {loading && !stats ? <div className="loading">טוען...</div> : (
                <>
                  <div className="stats-row">
                    <div className="stat-box"><span className="stat-num">{stats?.totalMessages || 0}</span><span className="stat-label">הודעות</span></div>
                    <div className="stat-box"><span className="stat-num">{stats?.activeChats || 0}</span><span className="stat-label">צ'אטים</span></div>
                    <div className="stat-box"><span className="stat-num">{connectedCount}/{activeMentors.length}</span><span className="stat-label">מחוברים</span></div>
                    <div className="stat-box"><span className="stat-num">{stats?.avgResponseTime || 0}</span><span className="stat-label">דק' תגובה</span></div>
                  </div>

                  <div className="heatmap-wrapper">
                    <h3>מפת פעילות שבועית</h3>
                    {renderHeatmap(getHeatmapData())}
                  </div>

                  <div className="activity-section">
                    <h3>פעילות אחרונה</h3>
                    <div className="activity-list">
                      {stats?.recentActivity?.slice(0, 10).map((a, i) => (
                        <div key={i} className="activity-item">
                          <span className="activity-name">{a.chatName}</span>
                          <span className="activity-mentor">{a.mentorName}</span>
                          <span className={`activity-type ${a.type}`}>{a.type === 'sent' ? '↑' : '↓'}</span>
                          <span className="activity-time">{a.time}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'mentors' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>{connectedCount}/{activeMentors.length} מחוברים</h3>
              </div>
              <div className="mentors-list-simple">
                {activeMentors.map((mentor) => (
                  <div key={mentor.id} className="mentor-row" onClick={() => setSelectedMentorModal(mentor)}>
                    <div className={`mentor-status ${mentor.isConnected ? 'online' : 'offline'}`}></div>
                    <span className="mentor-row-name">{mentor.name}</span>
                    <span className="mentor-row-stats">{mentor.totalMessages || 0} הודעות</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'messages' && (
            <div className="messages-tab">
              <div className="mentors-sidebar">
                <h3>מנטורים</h3>
                <div className="mentors-list">
                  {activeMentors.map((mentor) => (
                    <div key={mentor.id} className={`mentor-item ${selectedMentor?.id === mentor.id ? 'active' : ''}`} onClick={() => handleSelectMentor(mentor)}>
                      <div className={`status-dot ${mentor.isConnected ? 'online' : 'offline'}`}></div>
                      <span>{mentor.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="chats-sidebar">
                {selectedMentor ? (
                  <>
                    <div className="chats-header">
                      <input type="text" placeholder="חיפוש..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="search-input" />
                    </div>
                    <div className="chats-list">
                      {loadingChats ? <div className="loading-small">טוען...</div> :
                        filteredChats.map((chat) => (
                          <div key={chat.id} className={`chat-item ${selectedChat?.id === chat.id ? 'active' : ''}`} onClick={() => handleSelectChat(chat)}>
                            <span className="chat-name">{chat.name}</span>
                          </div>
                        ))
                      }
                    </div>
                  </>
                ) : <div className="empty-state">בחר מנטור</div>}
              </div>

              <div className="messages-view">
                {selectedChat ? (
                  <>
                    <div className="messages-header">
                      <span>{selectedChat.name}</span>
                      <span className="sub">{selectedMentor?.name}</span>
                    </div>
                    <div className="messages-container">
                      {loadingMessages ? <div className="loading-small">טוען...</div> :
                        messages.map((msg, i) => (
                          <div key={i} className={`message ${msg.fromMe ? 'sent' : 'received'}`}>
                            {msg.body && <p>{msg.body}</p>}
                            <span className="time">{formatMessageTime(msg.timestamp)}</span>
                          </div>
                        ))
                      }
                      <div ref={messagesEndRef} />
                    </div>
                  </>
                ) : <div className="empty-state">בחר צ'אט</div>}
              </div>
            </div>
          )}

          {activeTab === 'reports' && (
            <div className="tab-content reports-tab">
              <div className="report-config">
                <h3>הגדרות דוח</h3>

                <div className="config-group">
                  <label>סוג דוח:</label>
                  <div className="radio-group">
                    <label className={reportType === 'department' ? 'active' : ''}>
                      <input type="radio" name="reportType" value="department" checked={reportType === 'department'} onChange={(e) => setReportType(e.target.value)} />
                      כל המחלקה
                    </label>
                    <label className={reportType === 'mentor' ? 'active' : ''}>
                      <input type="radio" name="reportType" value="mentor" checked={reportType === 'mentor'} onChange={(e) => setReportType(e.target.value)} />
                      מנטור ספציפי
                    </label>
                  </div>
                </div>

                {reportType === 'mentor' && (
                  <div className="config-group">
                    <label>בחר מנטור:</label>
                    <select value={reportMentor} onChange={(e) => setReportMentor(e.target.value)}>
                      <option value="all">כל המנטורים</option>
                      {activeMentors.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                  </div>
                )}

                <div className="config-group">
                  <label>תקופה:</label>
                  <select value={reportDateRange} onChange={(e) => setReportDateRange(e.target.value)}>
                    <option value="today">היום</option>
                    <option value="week">שבוע אחרון</option>
                    <option value="month">חודש אחרון</option>
                  </select>
                </div>

                <button className="generate-btn" onClick={generateReport} disabled={generatingReport}>
                  {Icons.download}
                  {generatingReport ? 'מייצר דוח...' : 'הפק דוח PDF'}
                </button>
              </div>

              <div className="report-preview">
                <h3>תצוגה מקדימה</h3>
                <div className="preview-content" ref={reportRef}>
                  {reportType === 'department' ? (
                    <p>הדוח יכלול את כל {activeMentors.length} המנטורים עם מפות חום של הודעות שנשלחו והתקבלו.</p>
                  ) : (
                    <p>הדוח יכלול את המנטור הנבחר עם מפות חום של הודעות שנשלחו והתקבלו.</p>
                  )}

                  <div className="preview-heatmap">
                    <h4>דוגמה - מפת חום:</h4>
                    {renderHeatmap(getHeatmapData('received'))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Mentor Modal */}
      {selectedMentorModal && (
        <div className="modal-overlay" onClick={() => setSelectedMentorModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedMentorModal.name}</h2>
              <button onClick={() => setSelectedMentorModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className={`modal-status ${selectedMentorModal.isConnected ? 'online' : 'offline'}`}>
                {selectedMentorModal.isConnected ? '🟢 מחובר' : '🔴 מנותק'}
              </div>
              <div className="modal-stats">
                <div><strong>{selectedMentorModal.totalMessages || 0}</strong> הודעות</div>
                <div><strong>{selectedMentorModal.totalChats || 0}</strong> צ'אטים</div>
              </div>
              <p className="modal-meta">כניסה אחרונה: {selectedMentorModal.last_login ? new Date(selectedMentorModal.last_login).toLocaleDateString('he-IL') : 'לא ידוע'}</p>
            </div>
            <div className="modal-actions">
              <button className="btn primary" onClick={() => handleReconnectMentor(selectedMentorModal.id)} disabled={reconnectingMentor === selectedMentorModal.id}>
                {reconnectingMentor === selectedMentorModal.id ? '⏳' : '🔄'} חבר מחדש
              </button>
              <button className="btn danger" onClick={() => handleDeleteMentor(selectedMentorModal.id)} disabled={deletingMentor === selectedMentorModal.id}>
                {deletingMentor === selectedMentorModal.id ? '⏳' : '🗑️'} הסר
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Bottom Nav */}
      <nav className="bottom-nav">
        <button className={activeTab === 'overview' ? 'active' : ''} onClick={() => setActiveTab('overview')}>
          {Icons.overview}
          <span>סקירה</span>
        </button>
        <button className={activeTab === 'mentors' ? 'active' : ''} onClick={() => setActiveTab('mentors')}>
          {Icons.mentors}
          <span>מנטורים</span>
        </button>
        <button className={activeTab === 'messages' ? 'active' : ''} onClick={() => setActiveTab('messages')}>
          {Icons.messages}
          <span>הודעות</span>
        </button>
        <button className={activeTab === 'reports' ? 'active' : ''} onClick={() => setActiveTab('reports')}>
          {Icons.reports}
          <span>דוחות</span>
        </button>
      </nav>
    </div>
  );
}

export default AdminPanel;
